import { useEffect, useState } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Users, Plus, Edit, Trash2, Upload, Key, Mail, Search, Building2, Eye } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminUsersPage = () => {
  const { user } = useOutletContext();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: '',
    role: 'titular'
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [users, searchTerm, roleFilter]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/users`, { withCredentials: true });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Erro ao carregar utilizadores');
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = () => {
    let filtered = [...users];
    
    if (searchTerm) {
      filtered = filtered.filter(u => 
        u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (roleFilter !== 'all') {
      filtered = filtered.filter(u => u.role === roleFilter);
    }
    
    setFilteredUsers(filtered);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editUser) {
        const updateData = {
          name: formData.name,
          role: formData.role
        };
        
        // Only include email if changed
        if (formData.email !== editUser.email) {
          updateData.email = formData.email;
        }
        
        // Only include password if provided
        if (formData.password && formData.password.length > 0) {
          updateData.password = formData.password;
        }
        
        await axios.patch(`${BACKEND_URL}/api/admin/users/${editUser.user_id}`, updateData, { withCredentials: true });
        toast.success('Utilizador atualizado com sucesso');
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/users`, formData, { withCredentials: true });
        toast.success('Utilizador criado com sucesso');
      }
      
      setDialogOpen(false);
      setEditUser(null);
      setFormData({ email: '', name: '', password: '', role: 'titular' });
      fetchUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      toast.error(error.response?.data?.detail || 'Erro ao guardar utilizador');
    }
  };

  const handleEdit = (usr) => {
    setEditUser(usr);
    setFormData({
      email: usr.email,
      name: usr.name,
      password: '',
      role: usr.role
    });
    setDialogOpen(true);
  };

  const handleDeactivate = async (userId) => {
    if (!confirm('Tem a certeza que deseja desativar este utilizador?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/users/${userId}`, { withCredentials: true });
      toast.success('Utilizador desativado');
      fetchUsers();
    } catch (error) {
      console.error('Error deactivating user:', error);
      toast.error('Erro ao desativar utilizador');
    }
  };

  const handleReactivate = async (userId) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/admin/users/${userId}`, { is_active: true }, { withCredentials: true });
      toast.success('Utilizador reativado');
      fetchUsers();
    } catch (error) {
      console.error('Error reactivating user:', error);
      toast.error('Erro ao reativar utilizador');
    }
  };

  const handleImportUsers = async () => {
    if (!importFile) {
      toast.error('Selecione um ficheiro');
      return;
    }
    
    setImporting(true);
    const formData = new FormData();
    formData.append('file', importFile);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/import-users-bulk`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(`${response.data.created} titulares importados com sucesso! ${response.data.skipped} ignorados.`);
      setImportDialogOpen(false);
      setImportFile(null);
      fetchUsers();
    } catch (error) {
      console.error('Error importing users:', error);
      toast.error(error.response?.data?.detail || 'Erro ao importar utilizadores');
    } finally {
      setImporting(false);
    }
  };

  const getRoleBadge = (role) => {
    const styles = {
      admin: 'bg-red-50 text-red-700 border-red-200',
      dpo: 'bg-purple-50 text-purple-700 border-purple-200',
      operador: 'bg-blue-50 text-blue-700 border-blue-200',
      titular: 'bg-slate-50 text-slate-700 border-slate-200'
    };
    
    const labels = {
      admin: 'ADMIN',
      dpo: 'DPO',
      operador: 'OPERADOR',
      titular: 'TITULAR'
    };
    
    return <Badge className={styles[role] || styles.titular}>{labels[role] || role.toUpperCase()}</Badge>;
  };

  const userStats = {
    total: users.length,
    active: users.filter(u => u.is_active !== false).length,
    admins: users.filter(u => u.role === 'admin').length,
    dpos: users.filter(u => u.role === 'dpo').length,
    operadores: users.filter(u => u.role === 'operador').length,
    titulares: users.filter(u => u.role === 'titular').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#10B981]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-users-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-[#0F172A] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Gestão de Utilizadores
          </h1>
          <p className="text-slate-600">
            Criar, editar e gerir todos os utilizadores da plataforma
          </p>
        </div>
        
        <div className="flex gap-3">
          <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="import-users-button" variant="outline" className="border-[#3B82F6] text-[#3B82F6]">
                <Upload className="w-4 h-4 mr-2" />
                Importar Titulares
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Importar Titulares em Massa</DialogTitle>
                <DialogDescription>
                  Carregar ficheiro CSV ou Excel com os dados dos titulares
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="import-file">Ficheiro (CSV, Excel)</Label>
                  <Input
                    id="import-file"
                    data-testid="import-file-input"
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={(e) => setImportFile(e.target.files[0])}
                    className="mt-2"
                  />
                </div>
                
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-[#0F172A] mb-2">Formato do Ficheiro:</h4>
                  <p className="text-sm text-slate-600 mb-2">O ficheiro deve conter as seguintes colunas:</p>
                  <ul className="text-sm text-slate-600 space-y-1">
                    <li>• <strong>email</strong> (obrigatório)</li>
                    <li>• <strong>name</strong> ou <strong>nome</strong> (obrigatório)</li>
                    <li>• <strong>password</strong> ou <strong>senha</strong> (opcional)</li>
                  </ul>
                </div>
                
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setImportDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button
                    data-testid="submit-import-button"
                    onClick={handleImportUsers}
                    disabled={!importFile || importing}
                    className="bg-[#3B82F6] hover:bg-[#3B82F6]/90"
                  >
                    {importing ? 'A importar...' : 'Importar'}
                  </Button>
                </DialogFooter>
              </div>
            </DialogContent>
          </Dialog>
          
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) {
              setEditUser(null);
              setFormData({ email: '', name: '', password: '', role: 'titular' });
            }
          }}>
            <DialogTrigger asChild>
              <Button data-testid="create-user-button" className="bg-[#10B981] hover:bg-[#10B981]/90">
                <Plus className="w-4 h-4 mr-2" />
                Novo Utilizador
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editUser ? 'Editar Utilizador' : 'Criar Novo Utilizador'}</DialogTitle>
                <DialogDescription>
                  {editUser ? 'Atualizar informações do utilizador incluindo email e password' : 'Preencha os dados para criar um novo utilizador'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name">Nome Completo *</Label>
                  <Input
                    id="name"
                    data-testid="user-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    placeholder="João Silva"
                  />
                </div>
                
                <div>
                  <Label htmlFor="email" className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Email {editUser ? '' : '*'}
                  </Label>
                  <Input
                    id="email"
                    data-testid="user-email-input"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required={!editUser}
                    placeholder="joao@exemplo.com"
                  />
                  {editUser && (
                    <p className="text-xs text-slate-500 mt-1">Alterar o email do utilizador se necessário</p>
                  )}
                </div>
                
                <div>
                  <Label htmlFor="password" className="flex items-center gap-2">
                    <Key className="w-4 h-4" />
                    Password {editUser ? '(deixe vazio para manter)' : '*'}
                  </Label>
                  <Input
                    id="password"
                    data-testid="user-password-input"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    required={!editUser}
                    placeholder={editUser ? 'Nova password (opcional)' : 'Mínimo 8 caracteres'}
                  />
                  {editUser && (
                    <p className="text-xs text-slate-500 mt-1">Deixe vazio para manter a password atual</p>
                  )}
                </div>
                
                <div>
                  <Label htmlFor="role">Função *</Label>
                  <Select value={formData.role} onValueChange={(value) => setFormData({...formData, role: value})}>
                    <SelectTrigger data-testid="user-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="titular">Titular</SelectItem>
                      <SelectItem value="operador">Operador</SelectItem>
                      <SelectItem value="dpo">DPO</SelectItem>
                      <SelectItem value="admin">Administrador</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button data-testid="submit-user-button" type="submit" className="bg-[#10B981] hover:bg-[#10B981]/90">
                    {editUser ? 'Atualizar' : 'Criar'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Card className="border-slate-200">
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold text-[#0F172A]">{userStats.total}</div>
            <p className="text-xs text-slate-600">Total</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold text-emerald-600">{userStats.active}</div>
            <p className="text-xs text-slate-600">Ativos</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 bg-red-50">
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold text-red-700">{userStats.admins}</div>
            <p className="text-xs text-red-600">Admins</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 bg-purple-50">
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold text-purple-700">{userStats.dpos}</div>
            <p className="text-xs text-purple-600">DPOs</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 bg-blue-50">
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold text-blue-700">{userStats.operadores}</div>
            <p className="text-xs text-blue-600">Operadores</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold text-slate-700">{userStats.titulares}</div>
            <p className="text-xs text-slate-600">Titulares</p>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            data-testid="search-users-input"
            placeholder="Pesquisar por nome ou email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-full md:w-48" data-testid="filter-role-select">
            <SelectValue placeholder="Filtrar por função" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as funções</SelectItem>
            <SelectItem value="admin">Administradores</SelectItem>
            <SelectItem value="dpo">DPOs</SelectItem>
            <SelectItem value="operador">Operadores</SelectItem>
            <SelectItem value="titular">Titulares</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Users List */}
      <div className="grid grid-cols-1 gap-4">
        {filteredUsers.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="py-12 text-center">
              <Users className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-[#0F172A] mb-2">Nenhum utilizador encontrado</h3>
              <p className="text-slate-600">
                {searchTerm || roleFilter !== 'all' ? 'Tente ajustar os filtros de pesquisa.' : 'Crie o primeiro utilizador para começar.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredUsers.map((usr) => (
            <Card key={usr.user_id} className={`border-slate-200 ${!usr.is_active ? 'opacity-60' : ''}`} data-testid={`user-card-${usr.user_id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg flex items-center gap-2">
                      {usr.name}
                      {usr.user_id === user.user_id && (
                        <Badge variant="outline" className="text-xs">Você</Badge>
                      )}
                    </CardTitle>
                    <p className="text-sm text-slate-600 mt-1 flex items-center gap-2">
                      <Mail className="w-3 h-3" />
                      {usr.email}
                    </p>
                    {usr.tenant_name && (
                      <p className="text-sm text-blue-600 mt-1 flex items-center gap-2">
                        <Building2 className="w-3 h-3" />
                        {usr.tenant_name}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {getRoleBadge(usr.role)}
                    {usr.is_active === false && (
                      <Badge className="bg-red-50 text-red-700 border-red-200">Inativo</Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="text-sm space-y-1">
                    <div>
                      <span className="text-slate-600">Autenticação:</span>
                      <span className="ml-2 font-medium text-[#0F172A]">{usr.auth_method || 'local'}</span>
                    </div>
                    <div>
                      <span className="text-slate-600">Criado:</span>
                      <span className="ml-2 font-medium text-[#0F172A]">
                        {new Date(usr.created_at).toLocaleDateString('pt-PT')}
                      </span>
                    </div>
                  </div>
                  
                  {usr.user_id !== user.user_id && (
                    <div className="flex gap-2">
                      <Button
                        data-testid={`edit-user-${usr.user_id}`}
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(usr)}
                        title="Editar utilizador (nome, email, password, função)"
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Editar
                      </Button>
                      {usr.is_active !== false ? (
                        <Button
                          data-testid={`deactivate-user-${usr.user_id}`}
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDeactivate(usr.user_id)}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Desativar
                        </Button>
                      ) : (
                        <Button
                          data-testid={`reactivate-user-${usr.user_id}`}
                          variant="outline"
                          size="sm"
                          onClick={() => handleReactivate(usr.user_id)}
                          className="border-emerald-500 text-emerald-600"
                        >
                          Reativar
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminUsersPage;
